import { zodResolver } from '@hookform/resolvers/zod';
import { Camera, ListPlus, Loader, Save, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import { useAddPropertyAttribute } from '../hooks/useAddPropertyAttribute';
import { useUpdatePropertyAttribute } from '../hooks/useUpdatePropertyAttribute';
import { propertyAttributeServices } from '../services/propertyAttribute.service';

const TYPE_OPTIONS = [
  { value: 'amenity', label: 'Amenity' },
  { value: 'additionalRoom', label: 'Additional Room' },
  { value: 'overlooking', label: 'Overlooking' },
  { value: 'propertyType', label: 'Property Type' },
];

const attributeSchema = z.object({
  type: z.enum(['amenity', 'additionalRoom', 'overlooking', 'propertyType'], {
    errorMap: () => ({ message: 'Select a type' }),
  }),
  key: z.string().optional(),
  label: z.string().min(1, 'Label is required'),
  displayOrder: z.coerce.number().optional(),
  isActive: z.boolean().optional(),
});

// Handles both Add and Edit — pass `attribute` (an existing record) to edit,
// or leave it null/undefined to create a new one. Same pattern as this
// app's other add/edit-combined forms.
const PropertyAttributeFormModal = ({ isOpen, setIsOpen, attribute, defaultType }) => {
  const isEditMode = !!attribute?._id;
  const { mutate: addAttribute, isPending: isAdding } = useAddPropertyAttribute();
  const { mutate: updateAttribute, isPending: isUpdating } = useUpdatePropertyAttribute();
  const isPending = isAdding || isUpdating;

  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(attributeSchema),
    mode: 'onChange',
    defaultValues: {
      type: defaultType || 'amenity',
      key: '',
      label: '',
      displayOrder: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      reset({
        type: attribute.type,
        key: attribute.key,
        label: attribute.label,
        displayOrder: attribute.displayOrder ?? 0,
        isActive: attribute.isActive ?? true,
      });
      setIconPreview(attribute.icon || null);
    } else {
      reset({
        type: defaultType || 'amenity',
        key: '',
        label: '',
        displayOrder: 0,
        isActive: true,
      });
      setIconPreview(null);
    }
    setIconFile(null);
  }, [isOpen, isEditMode, attribute, defaultType, reset]);

  const validateFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Icon size should be less than 2MB');
      return false;
    }
    return true;
  };

  const handleIconSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setIconFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setIconPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    if (iconInputRef.current) iconInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    handleRemoveIcon();
    setIsOpen(false);
  };

  const onSubmit = async (data) => {
    try {
      let iconKey = isEditMode ? attribute.icon : '';

      if (iconFile) {
        setIsUploadingIcon(true);
        const uploadResponse = await propertyAttributeServices.uploadFile(iconFile);
        iconKey = uploadResponse?.data?.key || '';
        setIsUploadingIcon(false);
      } else if (isEditMode && !iconPreview) {
        // User explicitly removed the existing icon
        iconKey = '';
      }

      const payload = {
        ...data,
        icon: iconKey,
      };

      if (isEditMode) {
        // key/type intentionally not sent on update — a stored key must
        // keep matching whatever properties already reference it.
        const { type, key, ...updatePayload } = payload;
        updateAttribute(
          { id: attribute._id, data: updatePayload },
          { onSuccess: handleClose },
        );
      } else {
        addAttribute(payload, { onSuccess: handleClose });
      }
    } catch (error) {
      setIsUploadingIcon(false);
      toast.error(error?.response?.data?.message || 'Failed to upload icon');
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto custom-scrollbar'>
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>
            {isEditMode ? 'Edit Property Attribute' : 'Add Property Attribute'}
          </h2>
          <button onClick={handleClose} className='p-1 hover:bg-muted rounded-md transition-colors'>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-5'>
          <div>
            <label className='block text-sm font-medium mb-1'>Type *</label>
            <select
              {...register('type')}
              disabled={isEditMode}
              className='w-full px-3 py-2 text-sm disabled:opacity-60'
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {isEditMode && (
              <p className='text-xs text-muted-foreground mt-1'>Type can't be changed after creation.</p>
            )}
            {errors.type && <p className='text-destructive text-xs mt-1'>{errors.type.message}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>Label *</label>
            <input
              type='text'
              {...register('label')}
              className='w-full px-3 py-2 text-sm'
              placeholder='e.g., Swimming Pool'
            />
            {errors.label && <p className='text-destructive text-xs mt-1'>{errors.label.message}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>
              Key {!isEditMode && <span className='text-xs text-muted-foreground'>(auto-generated from label if left blank)</span>}
            </label>
            <input
              type='text'
              {...register('key')}
              disabled={isEditMode}
              className='w-full px-3 py-2 text-sm disabled:opacity-60'
              placeholder='e.g., swimmingPool'
            />
            {isEditMode && (
              <p className='text-xs text-muted-foreground mt-1'>
                Key can't be changed after creation — existing properties reference it.
              </p>
            )}
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>Display Order</label>
            <input
              type='number'
              {...register('displayOrder')}
              className='w-full px-3 py-2 text-sm'
              placeholder='0'
            />
          </div>

          <div>
            <label className='block text-sm font-medium mb-2'>Icon</label>
            {iconPreview ? (
              <div className='flex items-center space-x-4'>
                <img
                  src={iconPreview}
                  alt='Icon preview'
                  className='w-16 h-16 rounded-lg object-cover border border-border bg-muted'
                />
                <div className='flex flex-col space-y-2'>
                  {iconFile?.name && <p className='text-xs text-muted-foreground'>{iconFile.name}</p>}
                  <button
                    type='button'
                    onClick={handleRemoveIcon}
                    className='flex items-center space-x-1 text-xs text-destructive hover:text-destructive/80'
                  >
                    <Trash2 size={14} />
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => iconInputRef.current?.click()}
                className='border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors'
              >
                <input
                  ref={iconInputRef}
                  type='file'
                  accept='image/*'
                  onChange={(e) => handleIconSelect(e.target.files?.[0])}
                  className='hidden'
                />
                <div className='flex flex-col items-center space-y-2'>
                  <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                    <Camera size={20} className='text-primary' />
                  </div>
                  <p className='text-sm text-muted-foreground'>Click to upload icon</p>
                  <p className='text-xs text-muted-foreground'>PNG, JPG up to 2MB — shown next to this option in both apps</p>
                </div>
              </div>
            )}
          </div>

          <div className='flex items-center space-x-2'>
            <input type='checkbox' id='isActive' {...register('isActive')} className='h-4 w-4' />
            <label htmlFor='isActive' className='text-sm font-medium'>
              Active (visible to providers and customers)
            </label>
          </div>

          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={handleClose}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isPending || isUploadingIcon}
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
            >
              {isPending || isUploadingIcon ? (
                <div className='flex items-center justify-center'>
                  <Loader size={16} className='animate-spin mr-2' />
                  {isUploadingIcon ? 'Uploading...' : isEditMode ? 'Saving...' : 'Creating...'}
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  {isEditMode ? <Save size={16} className='mr-2' /> : <ListPlus size={16} className='mr-2' />}
                  {isEditMode ? 'Save Changes' : 'Create Attribute'}
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyAttributeFormModal;
